// src/lib/api/client.ts
import { API_CONFIG } from '@/config';
import { AppError } from '@/utils/errors/AppError';

interface ApiOptions extends RequestInit {
  params?: Record<string, string>;
  retry?: {
    attempts: number;
    delay: number;
  };
}

class ApiClient {
  private baseUrl: string;
  private defaultHeaders: HeadersInit;

  constructor() {
    this.baseUrl = '/api';
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
  }

  private getAuthHeader(): HeadersInit {
    const token = localStorage.getItem('sessionToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const data = await response.json();
    
    if (!response.ok) {
      throw new AppError(
        data.error || 'Request failed',
        response.status,
        data.details
      );
    }

    return data.data;
  }

  async get<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const url = new URL(endpoint, window.location.origin + this.baseUrl);
    if (options.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    const response = await fetch(url.toString(), {
      ...options,
      headers: {
        ...this.defaultHeaders,
        ...this.getAuthHeader(),
        ...options.headers
      }
    });

    return this.handleResponse<T>(response);
  }

  async post<T>(endpoint: string, data?: unknown, options: ApiOptions = {}): Promise<T> {
    const url = new URL(endpoint, window.location.origin + this.baseUrl);
    const response = await fetch(url.toString(), {
      ...options,
      method: 'POST',
      headers: {
        ...this.defaultHeaders,
        ...this.getAuthHeader(),
        ...options.headers
      },
      body: data ? JSON.stringify(data) : undefined
    });

    return this.handleResponse<T>(response);
  }

  async patch<T>(endpoint: string, data?: unknown, options: ApiOptions = {}): Promise<T> {
    const url = new URL(endpoint, window.location.origin + this.baseUrl);
    const response = await fetch(url.toString(), {
      ...options,
      method: 'PATCH',
      headers: {
        ...this.defaultHeaders,
        ...this.getAuthHeader(),
        ...options.headers
      },
      body: data ? JSON.stringify(data) : undefined
    });

    return this.handleResponse<T>(response);
  }

  async delete<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const url = new URL(endpoint, window.location.origin + this.baseUrl);
    const response = await fetch(url.toString(), {
      ...options,
      method: 'DELETE',
      headers: {
        ...this.defaultHeaders,
        ...this.getAuthHeader(),
        ...options.headers
      }
    });

    return this.handleResponse<T>(response);
  }
}

export const api = new ApiClient();
